import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, BookOpen, Loader2, Trash2, Share2, Copy, ShieldOff, ListVideo } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AiNotesPanel } from "@/components/video-notes/AiNotesPanel";
import ManualNotesEditor from "@/components/video-notes/ManualNotesEditor";

function formatTimestamp(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function RecordingDetailPage() {
  const { recordingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const [recording, setRecording] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("ai_notes");
  const [shareUrl, setShareUrl] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);

  const load = useCallback(() => api.get(`/meetings/recordings/${recordingId}`).then((res) => setRecording(res.data)), [recordingId]);

  useEffect(() => {
    setLoading(true);
    load().catch(() => toast.error("Recording not found")).finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    if (recording?.transcript_status !== "processing") return;
    const t = setTimeout(load, 6000);
    return () => clearTimeout(t);
  }, [recording, load]);

  const isHost = !!(user && recording && user.user_id === recording.host_id);

  const handleDelete = async () => {
    try {
      await api.delete(`/meetings/recordings/${recordingId}`);
      toast.success("Recording deleted");
      navigate("/meetings/recordings");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleShare = async () => {
    setShareLoading(true);
    try {
      const res = await api.post(`/meetings/recordings/${recordingId}/share`);
      setShareUrl(res.data.share_url);
      setRecording((prev) => ({ ...prev, is_shared: true }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setShareLoading(false);
    }
  };

  const handleRevoke = async () => {
    try {
      await api.delete(`/meetings/recordings/${recordingId}/share`);
      setShareUrl(null);
      setRecording((prev) => ({ ...prev, is_shared: false }));
      toast.success("Access revoked — the old link stopped working");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied");
  };

  const seekTo = (seconds) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = seconds;
    videoRef.current.play().catch(() => {});
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24" data-testid="recording-detail-loading"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  }
  if (!recording) return null;

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8" data-testid="recording-detail-page">
      <button onClick={() => navigate("/meetings/recordings")} data-testid="recording-detail-back-btn" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Recordings
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <h1 className="font-heading text-2xl font-semibold text-white">{recording.title}</h1>
        {isHost && (
          <button onClick={handleDelete} data-testid="recording-detail-delete-btn" className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-rose-400">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        )}
      </div>

      <div className="w-full aspect-video rounded-2xl bg-black/40 border border-white/10 overflow-hidden mb-5" data-testid="recording-video-player">
        <video ref={videoRef} src={`${api.defaults.baseURL}/meetings/recordings/${recordingId}/stream`} controls className="w-full h-full" data-testid="recording-video-element" />
      </div>

      {isHost && (
        <div className="glass-panel rounded-2xl p-4 mb-5" data-testid="recording-share-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Share2 className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-semibold text-white">Share Access</span>
              {recording.is_shared && (
                <span data-testid="recording-share-active-badge" className="text-[10px] font-bold uppercase text-emerald-300 bg-emerald-400/10 px-1.5 py-0.5 rounded-full">Link Active</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!shareUrl && (
                <button onClick={handleShare} disabled={shareLoading} data-testid="recording-share-btn"
                  className="flex items-center gap-1.5 text-xs font-semibold text-cyan-300 hover:text-cyan-200 bg-cyan-400/10 hover:bg-cyan-400/15 border border-cyan-400/20 rounded-lg px-3 py-1.5 disabled:opacity-60 transition-colors">
                  {shareLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                  {recording.is_shared ? "Get Share Link" : "Share Recording"}
                </button>
              )}
              {recording.is_shared && (
                <button onClick={handleRevoke} data-testid="recording-revoke-share-btn"
                  className="flex items-center gap-1.5 text-xs font-semibold text-rose-400 hover:text-rose-300 bg-rose-400/10 hover:bg-rose-400/15 border border-rose-400/20 rounded-lg px-3 py-1.5 transition-colors">
                  <ShieldOff className="w-3.5 h-3.5" /> Revoke Access
                </button>
              )}
            </div>
          </div>
          {shareUrl && (
            <div className="mt-3 flex items-center gap-2">
              <input readOnly value={shareUrl} data-testid="recording-share-url-input" onFocus={(e) => e.target.select()}
                className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-300 outline-none" />
              <button onClick={handleCopy} data-testid="recording-copy-share-btn"
                className="flex items-center gap-1.5 text-xs font-semibold text-white bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 transition-colors">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-2">Only you (the host) control this — anyone with the link can watch the recording & AI notes, no login needed. Revoke anytime to kill access instantly.</p>
        </div>
      )}

      {recording.highlights?.length > 0 && (
        <div className="glass-panel rounded-2xl p-4 mb-5" data-testid="recording-highlights-panel">
          <h4 className="text-xs font-bold uppercase tracking-wider text-cyan-300 mb-2 flex items-center gap-1.5">
            <ListVideo className="w-3.5 h-3.5" /> Highlights
          </h4>
          <div className="space-y-1">
            {recording.highlights.map((h, i) => (
              <button key={i} onClick={() => seekTo(h.timestamp)} data-testid={`recording-highlight-${i}`}
                className="w-full flex items-center gap-2.5 text-left text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-lg px-2 py-1.5 transition-colors">
                <span className="text-xs font-mono text-cyan-400 shrink-0 bg-cyan-400/10 rounded px-1.5 py-0.5">{formatTimestamp(h.timestamp)}</span>
                {h.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="glass-panel rounded-2xl overflow-hidden">
        <div className="flex border-b border-white/10">
          <button onClick={() => setTab("ai_notes")} data-testid="recording-tab-ai-notes"
            className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-3 transition-colors ${tab === "ai_notes" ? "text-amber-300 border-b-2 border-amber-300" : "text-slate-400 hover:text-white"}`}>
            <Sparkles className="w-4 h-4" /> AI Voice Notes
          </button>
          <button onClick={() => setTab("manual_notes")} data-testid="recording-tab-manual-notes"
            className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-3 transition-colors ${tab === "manual_notes" ? "text-amber-300 border-b-2 border-amber-300" : "text-slate-400 hover:text-white"}`}>
            <BookOpen className="w-4 h-4" /> Manual Notes
          </button>
        </div>
        <div className="p-5">
          {tab === "ai_notes" && <AiNotesPanel video={recording} />}
          {tab === "manual_notes" && (
            <ManualNotesEditor
              endpoint={`/meetings/recordings/${recordingId}/notes`}
              initialNotes={recording.manual_notes}
              onSaved={(notes) => setRecording((prev) => ({ ...prev, manual_notes: notes }))}
            />
          )}
        </div>
      </div>
    </div>
  );
}
